import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../../services/api";
import PrescriptionPrintCombined from "./PrescriptionPrintCombined";

export default function PrescriptionPrintRouter() {
  const { rxId }       = useParams();
  const [rx, setRx]    = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      if (e.data?.type === "RX_PRINT") setRx(e.data.payload);
    };
    window.addEventListener("message", handler);
    if (window.opener) window.opener.postMessage({ type: "PRINT_READY" }, "*");
    if (rxId && rxId !== "live") {
      api.get(`/prescriptions/${rxId}`)
        .then(res => setRx(res.data))
        .catch(() => {});
    }
    return () => window.removeEventListener("message", handler);
  }, [rxId]);

  useEffect(() => {
    if (!rx) return;
    const t = setTimeout(() => setReady(true), 600);
    return () => clearTimeout(t);
  }, [rx]);

  if (!rx) {
    return (
      <div style={{
        display:"flex", flexDirection:"column", alignItems:"center",
        justifyContent:"center", height:"100vh", background:"#f8fafc",
        fontFamily:"system-ui,sans-serif", gap:16,
      }}>
        <div style={{fontSize:40}}>🏥</div>
        <p style={{fontSize:16,color:"#64748b"}}>Preparing prescription preview…</p>
        <p style={{fontSize:11,color:"#94a3b8"}}>If this takes too long, close and try again.</p>
      </div>
    );
  }

  const patient = rx?.patient || {};
  const rxCode  = rx?.prescriptionId || "—";
  const rxDate  = rx?.createdAt
    ? new Date(rx.createdAt).toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"numeric"})
    : new Date().toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"numeric"});

  const handlePrint = () => window.print();

  const handleDownloadPDF = () => {
    const old = document.title;
    document.title = `${patient.name || "prescription"}_${rxCode}`;
    window.print();
    setTimeout(() => { document.title = old; }, 1500);
  };

  return (
    <>
      {/*
        PRINT CSS STRATEGY
        ──────────────────
        We use visibility:hidden on body (keeps layout, hides ink),
        then visibility:visible on ONLY #rx-print-content.
        This works regardless of React's DOM nesting depth —
        visibility is inherited so any visible ancestor is enough.

        @page { margin:0 } removes the browser's own date/URL/page-number headers.
        size:A4 portrait ensures correct paper dimensions.

        The toolbar div gets display:none on print so it takes no space.
      */}
      <style>{`
        @media screen {
          body {
            margin: 0;
            background: #b0bec5;
            font-family: system-ui, sans-serif;
          }
          #rx-print-content > div {
            box-shadow: 0 6px 32px rgba(0,0,0,0.25);
            margin-bottom: 24px;
          }
        }

        @media print {
          @page {
            size: A4 portrait;
            margin: 0;
          }

          body {
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
            visibility: hidden;
          }

          /* Make only the prescription content visible.
             Because visibility is inherited, all children
             of #rx-print-content become visible too. */
          #rx-print-content,
          #rx-print-content * {
            visibility: visible !important;
          }

          /* Position it at top-left of the page */
          #rx-print-content {
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            width: 210mm !important;
          }

          /* Remove shadows/margins added for screen view */
          #rx-print-content > div {
            box-shadow: none !important;
            margin: 0 !important;
          }

          /* Hide the toolbar completely */
          #rx-toolbar {
            display: none !important;
          }

          /* ── Header / footer / patient-info / body must always render ──
             Explicitly forced (not just inherited visibility) so the
             clinic logo, doctor credentials, and address blocks in the
             header, and the signature/footer text, can never end up
             missing from the printed page. */
          #rx-print-content #rx-print-header,
          #rx-print-content #rx-print-footer,
          #rx-print-content #rx-print-patient,
          #rx-print-content #rx-print-body {
            display: block !important;
            visibility: visible !important;
          }

          /* Hide any stray input borders/action controls that might be
             nested inside the printable area, while leaving all header,
             body, and footer content fully intact. */
          #rx-print-content input,
          #rx-print-content textarea,
          #rx-print-content select {
            border: none !important;
            box-shadow: none !important;
          }
          #rx-print-content button,
          #rx-print-content .no-print {
            display: none !important;
          }
        }
      `}</style>

      {/* TOOLBAR — hidden on print via #rx-toolbar display:none */}
      <div id="rx-toolbar" style={{
        position:"fixed", top:0, left:0, right:0, height:56,
        background:"#1e293b", display:"flex", alignItems:"center",
        justifyContent:"space-between", padding:"0 20px",
        zIndex:9999, boxShadow:"0 2px 10px rgba(0,0,0,0.5)",
      }}>
        <div style={{color:"#94a3b8",fontSize:13,display:"flex",gap:8,alignItems:"center"}}>
          <span style={{color:"#f1f5f9",fontWeight:700}}>{patient.name || "Preview"}</span>
          {patient.age    && <span>{patient.age} yrs</span>}
          {patient.gender && <span>· {patient.gender}</span>}
          {rxCode !== "—" && (
            <span style={{color:"#475569",fontSize:11}}>· {rxCode} · {rxDate}</span>
          )}
        </div>

        <div style={{display:"flex",gap:8}}>
          <button onClick={() => window.close()} style={{
            padding:"6px 12px", background:"transparent",
            border:"1px solid #475569", color:"#94a3b8",
            borderRadius:7, cursor:"pointer", fontSize:12,
          }}>
            ✕ Close
          </button>
          <button onClick={handleDownloadPDF} style={{
            padding:"6px 14px", background:"#0f172a",
            border:"1px solid #334155", color:"#cbd5e1",
            borderRadius:7, cursor:"pointer", fontSize:12,
          }}>
            ⬇ Download PDF
          </button>
          <button onClick={handlePrint} disabled={!ready} style={{
            padding:"6px 18px",
            background: ready ? "#16a34a" : "#166534",
            border:"none", color:"#fff", borderRadius:7,
            cursor: ready ? "pointer" : "not-allowed",
            fontSize:13, fontWeight:700, opacity: ready ? 1 : 0.6,
          }}>
            {ready ? "🖨 Print" : "⏳ Loading…"}
          </button>
        </div>
      </div>

      {/* PREVIEW AREA */}
      <div style={{
        paddingTop:68, paddingBottom:48,
        display:"flex", flexDirection:"column",
        alignItems:"center", minHeight:"100vh",
      }}>
        {/* id="rx-print-content" — this is what visibility:visible targets on print */}
        <div id="rx-print-content">
          <PrescriptionPrintCombined rx={rx} />
        </div>

        <p style={{marginTop:16,color:"#546e7a",fontSize:11,fontFamily:"system-ui"}}>
          Verify above · click <b style={{color:"#eceff1"}}>🖨 Print</b> for printer · or <b style={{color:"#eceff1"}}>⬇ Download PDF</b> to save
        </p>
      </div>
    </>
  );
}